import { useState } from "react";
import { useActivityReport } from "@/features/admin/admin";
import { dollars } from "@/features/payments/payments.types";
import { StatGridSkeleton } from "@/components/Skeletons";
import { sectionTitleClass } from "@/lib/typography";
import { timeAgo } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const LABELS: Record<string, string> = {
  logins: "Logins",
  active_members: "Active members",
  profile_views: "Profile views",
  thread_views: "Discussion views",
  resource_clicks: "Resource clicks",
  searches: "Searches",
  applications: "Applications",
  activations: "Activations",
  page_views: "Page views",
  checkouts_started: "Checkouts started",
  renewals: "Renewals",
  failures: "Payment failures",
  cancellations: "Cancellations",
  portal_opens: "Portal opens",
  payment_count: "Payments",
  revenue_cents: "Revenue",
};

const RANGES = [7, 30, 90];

function StatGrid({
  entries,
  money,
}: {
  entries: [string, number][];
  money?: boolean;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {entries.map(([key, value]) => (
        <Card key={key} className="animate-item-enter">
          <CardHeader>
            <p className="text-2xl font-semibold tabular-nums">
              {money && key === "revenue_cents" ? dollars(value) : value}
            </p>
            <CardDescription>{LABELS[key] ?? key}</CardDescription>
          </CardHeader>
        </Card>
      ))}
    </div>
  );
}

function TopList({
  title,
  rows,
}: {
  title: string;
  rows: { target_id: string; label: string; count: number }[];
}) {
  if (!rows.length) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className={sectionTitleClass}>{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {rows.map((row) => (
          <div
            key={row.target_id}
            className="flex items-center justify-between gap-2 text-sm"
          >
            <span className="truncate">{row.label}</span>
            <Badge variant="secondary" className="shrink-0 text-xs">
              {row.count}
            </Badge>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/**
 * Usage and the payment funnel, on the dashboard rather than a tab of its own.
 *
 * "How many members are there" and "what happened this month" were two pages
 * answering one question, so the second is a section of the first now.
 */
export function ActivitySection() {
  const [days, setDays] = useState(30);
  const { data, isPending, error } = useActivityReport(days);

  return (
    <div className="mt-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-xl">Activity</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Usage and the payment funnel over the selected window.
          </p>
        </div>
        <Tabs value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <TabsList>
            {RANGES.map((option) => (
              <TabsTrigger key={option} value={String(option)}>
                {option} days
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {error && <p className="text-destructive mt-6 text-sm">{error.message}</p>}

      <section className="mt-8">
        <h2 className={sectionTitleClass}>Usage</h2>
        <div className="mt-3">
          {isPending || !data ? (
            <StatGridSkeleton />
          ) : (
            <StatGrid entries={Object.entries(data.totals)} />
          )}
        </div>
      </section>

      {data && (
        <>
          <section className="mt-8">
            <h2 className={sectionTitleClass}>Payment funnel</h2>
            <div className="mt-3">
              <StatGrid entries={Object.entries(data.payments)} money />
            </div>
          </section>

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            <TopList title="Top profiles" rows={data.top_profiles} />
            <TopList title="Top discussions" rows={data.top_threads} />
            <TopList title="Top resources" rows={data.top_resources} />
            {data.most_active_members.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className={sectionTitleClass}>
                    Most active members
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {data.most_active_members.map((m) => (
                    <div
                      key={m.member_id}
                      className="flex items-center justify-between gap-2 text-sm"
                    >
                      <span className="truncate">{m.name}</span>
                      <Badge variant="secondary" className="shrink-0 text-xs">
                        {m.count}
                      </Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>

          <section className="mt-8 pb-8">
            <h2 className={sectionTitleClass}>Recent events</h2>
            {data.truncated && (
              <p className="text-muted-foreground mt-1 text-xs">
                Showing the most recent events only.
              </p>
            )}
            <div className="mt-3 space-y-2">
              {data.recent.length === 0 ? (
                <p className="text-muted-foreground py-6 text-center text-sm">
                  Nothing recorded in this window.
                </p>
              ) : (
                data.recent.map((e) => (
                  <div
                    key={e.id}
                    className="flex flex-wrap items-center gap-2 text-sm"
                  >
                    <Badge variant="outline" className="text-xs">
                      {e.type}
                    </Badge>
                    <span>{e.member_name}</span>
                    {e.target_label && (
                      <span className="text-muted-foreground truncate">
                        · {e.target_label}
                      </span>
                    )}
                    <span
                      className="text-muted-foreground ml-auto text-xs"
                      title={new Date(e.created_at).toLocaleString()}
                    >
                      {timeAgo(e.created_at)}
                    </span>
                  </div>
                ))
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
